import { Link, useLocation } from "wouter";
import { 
  Activity, Play, Users, CheckCircle, DollarSign, Briefcase, 
  Settings, Server, Wrench, ShieldAlert, BookOpen, MessageSquare, LogOut, ChevronLeft, Star
} from "lucide-react";
import { useAuth } from "@/lib/auth";

interface NavItem {
  title: string;
  url: string;
  icon: any;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

export const adminNavGroups: NavGroup[] = [
  {
    label: "Main",
    items: [
      { title: "Overview", url: "/", icon: Activity, exact: true },
      { title: "Runs", url: "/sessions", icon: Play },
      { title: "People", url: "/requests", icon: Users },
      { title: "Reviews", url: "/reviews", icon: Star },
      { title: "Feedback", url: "/feedback", icon: MessageSquare },
      { title: "Quality", url: "/evaluation", icon: CheckCircle }, // Will route to evaluation or benchmarks
      { title: "Money", url: "/billing", icon: DollarSign },
      { title: "Business", url: "/ip-assessment", icon: Briefcase },
    ],
  },
  {
    label: "System",
    items: [
      { title: "System Health", url: "/settings", icon: Settings },
      { title: "Advanced technical tools", url: "/resolver-telemetry", icon: Wrench },
    ],
  }
];

export const ADMIN_PUBLIC_ROOT_LINK = "~/";

export function isAdminNavItemActive(location: string, item: Pick<NavItem, "url" | "exact">): boolean {
  if (item.exact) {
    return location === item.url || location === item.url + "/";
  }

  return location === item.url || location.startsWith(item.url + "/");
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { logout } = useAuth();

  return (
    <div className="flex h-screen bg-[#0B0D10] text-[#F5F6F7]">
      <aside className="w-64 border-r border-white/10 bg-[#0F1216] flex flex-col">
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <span className="font-semibold text-[#F5F6F7] tracking-wide">RESONANCE</span>
          <span className="ml-2 text-xs text-[#707983] uppercase tracking-wider">Admin</span>
        </div>
        
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-8">
          {adminNavGroups.map((group) => (
            <div key={group.label}>
              <div className="text-xs font-medium text-[#707983] uppercase tracking-wider mb-3 px-2">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isActive = isAdminNavItemActive(location, item);
                    
                  return (
                    <Link key={item.title} href={item.url}>
                      <a className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? "bg-[#2DD4D8]/10 text-[#2DD4D8]" : "text-[#A9B0B8] hover:bg-white/5 hover:text-[#F5F6F7]"
                      }`}>
                        <item.icon className="w-4 h-4 shrink-0" />
                        {item.title}
                      </a>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 space-y-2">
          <Link href={ADMIN_PUBLIC_ROOT_LINK}>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[#A9B0B8] hover:bg-white/5 hover:text-[#F5F6F7] transition-colors">
              <ChevronLeft className="w-4 h-4 shrink-0" />
              Back to App
            </a>
          </Link>
          <button 
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[#A9B0B8] hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-[#0B0D10] p-8">
        <div className="max-w-6xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
